<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useExperienceStore } from './stores/experience'
import { syncPendingSubmissions } from './services/experience-service'

const route = useRoute()
const router = useRouter()
const experienceStore = useExperienceStore()

const INACTIVITY_WARNING_MS = 60_000
const RETURN_COUNTDOWN_SECONDS = 10

const warningVisible = ref(false)
const countdownSeconds = ref(RETURN_COUNTDOWN_SECONDS)
let inactivityTimer: number | undefined
let countdownTimer: number | undefined

function clearInactivityTimer() {
  if (inactivityTimer !== undefined) {
    window.clearTimeout(inactivityTimer)
    inactivityTimer = undefined
  }
}

function clearCountdownTimer() {
  if (countdownTimer !== undefined) {
    window.clearInterval(countdownTimer)
    countdownTimer = undefined
  }
}

function inactivityEnabled() {
  return route.name !== 'home' && route.meta.disableInactivity !== true
}

function returnHome() {
  clearInactivityTimer()
  clearCountdownTimer()
  warningVisible.value = false
  experienceStore.resetSession()
  void router.replace({ name: 'home' })
}

function showInactivityWarning() {
  if (!inactivityEnabled()) {
    return
  }

  warningVisible.value = true
  countdownSeconds.value = RETURN_COUNTDOWN_SECONDS
  clearCountdownTimer()

  countdownTimer = window.setInterval(() => {
    countdownSeconds.value -= 1
    if (countdownSeconds.value <= 0) {
      returnHome()
    }
  }, 1_000)
}

function restartInactivityTimer() {
  clearInactivityTimer()

  if (warningVisible.value) {
    warningVisible.value = false
    clearCountdownTimer()
  }

  if (!inactivityEnabled()) {
    return
  }

  inactivityTimer = window.setTimeout(showInactivityWarning, INACTIVITY_WARNING_MS)
}

function syncOfflineSubmissions() {
  void syncPendingSubmissions()
}

watch(() => route.fullPath, restartInactivityTimer)

onMounted(() => {
  window.addEventListener('pointerdown', restartInactivityTimer)
  window.addEventListener('keydown', restartInactivityTimer)
  window.addEventListener('input', restartInactivityTimer)
  window.addEventListener('online', syncOfflineSubmissions)
  syncOfflineSubmissions()
  restartInactivityTimer()
})

onBeforeUnmount(() => {
  clearInactivityTimer()
  clearCountdownTimer()
  window.removeEventListener('pointerdown', restartInactivityTimer)
  window.removeEventListener('keydown', restartInactivityTimer)
  window.removeEventListener('input', restartInactivityTimer)
  window.removeEventListener('online', syncOfflineSubmissions)
})
</script>

<template>
  <main class="app-stage">
    <section class="screen-shell">
      <RouterView />
    </section>

    <div
      v-if="warningVisible"
      class="inactivity-overlay"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="inactivity-title"
      @click="restartInactivityTimer"
    >
      <section class="inactivity-dialog">
        <p class="inactivity-kicker">温馨提示</p>
        <h2 id="inactivity-title">您已 60 秒未操作，即将返回首页！</h2>
        <p class="inactivity-countdown">
          <strong>{{ countdownSeconds }}</strong>
          秒后自动返回
        </p>
        <button class="primary-button inactivity-continue" type="button">
          继续操作
        </button>
        <p class="inactivity-hint">点击屏幕任意位置即可继续</p>
      </section>
    </div>
  </main>
</template>
